import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, TextField, InputAdornment, IconButton } from "@mui/material";
import {
  Search,
  Clear,
  AddRounded,
  ExtensionRounded,
  FileDownloadRounded,
  MoreVertRounded,
  Settings,
} from "@mui/icons-material";
import { HeaderActionsContext } from "../Layout";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import SettingsDialog from "../SettingsDialog";
import { useTheme } from "../../ThemeContext";
import { usePermissions } from "../../permissions/PermissionsProvider";
import { ACTIONS } from "../../permissions/definition";

const CircleIconButton = ({
  icon,
  tooltipId,
  tooltipContent,
  className = "",
  disabled,
  onClick,
}) => {
  return (
    <div className="relative flex justify-center">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        data-tooltip-id={tooltipId}
        className={`w-12 h-12 rounded-full flex items-center justify-center shadow-md transition-all duration-300 ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:scale-105"
        } ${className}`}
      >
        {icon}
      </button>
      <ReactTooltip
        id={tooltipId}
        place="bottom"
        effect="solid"
        className="tooltip-centered"
      >
        {tooltipContent}
      </ReactTooltip>
    </div>
  );
};

const OrdersToolbar = ({
  search,
  onSearchChange,
  onClearSearch,
  onOpenFilterDialog,
  onOpenAddProject,
  onOpenAddClient,
  onOpenEdit,
  onCreateInvoice,
  onDeleteProject,
  onExport,
  onGenerateSummary,
  selectedProjects,
  hasActiveFilters = false,
}) => {
  const { setHeaderActions } = useContext(HeaderActionsContext);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const { theme } = useTheme();
  const { can } = usePermissions();

  const addMenuRef = useRef(null);
  const moreMenuRef = useRef(null);
  const canAddProject = can(ACTIONS.PROJECTS_CREATE);
  const canAddClient = can(ACTIONS.CLIENTS_EDIT);
  const canExport = can(ACTIONS.PROJECTS_EXPORT);
  const canEdit = can(ACTIONS.PROJECTS_EDIT);
  const canCreateInvoice = can(ACTIONS.INVOICES_CREATE);
  const canDelete = can(ACTIONS.PROJECTS_DELETE);
  const canGenerateSummary = can(ACTIONS.FINANCIALS_VIEW);
  const canManageSettings = can(ACTIONS.SETTINGS_MANAGE_PERMISSIONS);
  const showActions =
    canAddProject ||
    canAddClient ||
    canExport ||
    canEdit ||
    canCreateInvoice ||
    canDelete ||
    canGenerateSummary ||
    canManageSettings;

  useEffect(() => {
    function handleClickOutside(e) {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
        setShowAddMenu(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const actionsNode = useMemo(() => {
    if (!showActions) return null;

    return (
      <div className="flex items-center gap-4">
        <div className="relative" ref={addMenuRef}>
          <CircleIconButton
            onClick={() => {
              setShowAddMenu((s) => !s);
              setShowMoreMenu(false);
            }}
            disabled={!canAddProject && !canAddClient}
            tooltipId="add-menu-tooltip"
            tooltipContent="Add..."
            className={theme.toolbarAddButton}
            icon={<AddRounded sx={{ fontSize: 30 }} aria-hidden="true" />}
          />
          {showAddMenu && (
            <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 max-w-[calc(100vw-40px)] rounded-xl border border-gray-200 bg-white shadow-lg z-20">
              <button
                type="button"
                onClick={() => {
                  if (!canAddProject) return;
                  setShowAddMenu(false);
                  onOpenAddProject();
                }}
                disabled={!canAddProject}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 rounded-t-xl ${
                  !canAddProject
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-slate-50"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-7 h-7 text-black mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 12v6m3-3h-6"
                  />
                </svg>
                <div>
                  <div className="text-sm font-medium text-gray-800">
                    Add Project
                  </div>
                  <div className="text-xs text-gray-500">
                    Create a new project
                  </div>
                </div>
              </button>
              <div className="h-px bg-gray-100" />
              <button
                type="button"
                onClick={() => {
                  if (!canAddClient) return;
                  setShowAddMenu(false);
                  onOpenAddClient();
                }}
                disabled={!canAddClient}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 rounded-b-xl ${
                  !canAddClient
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-slate-50"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-7 h-7 text-black mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z"
                  />
                </svg>
                <div>
                  <div className="text-sm font-medium text-gray-800">
                    Add Client
                  </div>
                  <div className="text-xs text-gray-500">
                    Create a new client
                  </div>
                </div>
              </button>
            </div>
          )}
        </div>

        <CircleIconButton
          onClick={onExport}
          disabled={!canExport}
          tooltipId="export-tooltip"
          tooltipContent="Export to Excel"
          className={theme.toolbarExportButton}
          icon={
            <FileDownloadRounded sx={{ fontSize: 30 }} aria-hidden="true" />
          }
        />

        {canManageSettings && (
          <CircleIconButton
            onClick={() => setShowSettings(true)}
            tooltipId="settings-tooltip"
            tooltipContent="Settings"
            className="bg-gray-200 text-gray-700 hover:bg-gray-300"
            icon={<Settings sx={{ fontSize: 24 }} aria-hidden="true" />}
          />
        )}

        {(canEdit || canCreateInvoice || canDelete || canGenerateSummary) && (
          <div className="relative" ref={moreMenuRef}>
            <CircleIconButton
              onClick={() => {
                setShowMoreMenu((s) => !s);
                setShowAddMenu(false);
              }}
              disabled={
                !canEdit &&
                !canCreateInvoice &&
                !canDelete &&
                !canGenerateSummary
              }
              tooltipId="more-menu-tooltip"
              tooltipContent="More actions"
              className="bg-gray-200 text-gray-700 hover:bg-gray-300"
              icon={
                <MoreVertRounded sx={{ fontSize: 28 }} aria-hidden="true" />
              }
            />
            {showMoreMenu && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 max-w-[calc(100vw-40px)] rounded-xl border border-gray-200 bg-white shadow-lg z-20 overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    if (selectedProjects.length !== 1 || !canEdit) return;
                    setShowMoreMenu(false);
                    onOpenEdit();
                  }}
                  disabled={selectedProjects.length !== 1 || !canEdit}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 rounded-t-xl ${
                    selectedProjects.length !== 1 || !canEdit
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-7 h-7 text-black mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                    />
                  </svg>
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      Edit
                    </div>
                    <div className="text-xs text-gray-500">
                      Edit selected project
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (selectedProjects.length < 1 || !canCreateInvoice) return;
                    setShowMoreMenu(false);
                    onCreateInvoice();
                  }}
                  disabled={selectedProjects.length < 1 || !canCreateInvoice}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 ${
                    selectedProjects.length < 1 || !canCreateInvoice
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-7 h-7 text-black mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
                    />
                  </svg>
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      Create Invoice
                    </div>
                    <div className="text-xs text-gray-500">
                      For selected project(s)
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (selectedProjects.length !== 1 || !canGenerateSummary)
                      return;
                    setShowMoreMenu(false);
                    onGenerateSummary();
                  }}
                  disabled={
                    selectedProjects.length !== 1 || !canGenerateSummary
                  }
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 ${
                    selectedProjects.length !== 1 || !canGenerateSummary
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-7 h-7 text-black mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M7 4h7l4 4v10a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14 4v4h4"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6M9 16h3"
                    />
                  </svg>
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      Generate Summary
                    </div>
                    <div className="text-xs text-gray-500">
                      Email-ready overview
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (selectedProjects.length !== 1 || !canDelete) return;
                    setShowMoreMenu(false);
                    onDeleteProject();
                  }}
                  disabled={selectedProjects.length !== 1 || !canDelete}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 rounded-b-xl ${
                    selectedProjects.length !== 1 || !canDelete
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-red-50"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-7 h-7 text-black mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                    />
                  </svg>
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      Delete Project
                    </div>
                    <div className="text-xs text-gray-500">
                      Remove selected project
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
        )}
        <SettingsDialog
          open={showSettings}
          onClose={() => setShowSettings(false)}
        />
      </div>
    );
  }, [
    canAddClient,
    canAddProject,
    canCreateInvoice,
    canDelete,
    canEdit,
    canExport,
    canGenerateSummary,
    canManageSettings,
    showActions,
    onCreateInvoice,
    onDeleteProject,
    onExport,
    onOpenAddClient,
    onOpenAddProject,
    onOpenEdit,
    onGenerateSummary,
    selectedProjects.length,
    showAddMenu,
    showMoreMenu,
    showSettings,
    theme,
  ]);

  useEffect(() => {
    setHeaderActions(actionsNode);
    return () => setHeaderActions(null);
  }, [actionsNode, setHeaderActions]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 1.5,
        mb: 4,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flex: 1,
          minWidth: 360,
          ml: 4,
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search"
          value={search}
          onChange={onSearchChange}
          sx={{
            flex: 1,
            maxWidth: 520,
            "& .MuiOutlinedInput-root": {
              borderRadius: "22px",
              height: "44px",
              padding: "0 12px",
              fontSize: "17px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={onClearSearch} size="small">
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <div className="relative">
          <IconButton
            onClick={onOpenFilterDialog}
            sx={{
              backgroundColor: hasActiveFilters ? "#dbeafe" : "#f1f3f6",
              "&:hover": {
                backgroundColor: hasActiveFilters ? "#bfdbfe" : "#e5e8ed",
              },
              border: hasActiveFilters ? "2px solid #3b82f6" : "none",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke={hasActiveFilters ? "#2563eb" : "currentColor"}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: 22, height: 22 }}
            >
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
          </IconButton>
          {hasActiveFilters && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white"></span>
          )}
        </div>
      </Box>
    </Box>
  );
};

export default OrdersToolbar;
